<template>
  <div class="game-management">
    <div class="header">
      <div class="header-left">
        <h1>游戏管理</h1>
      </div>
      <div class="header-right">
        <span v-if="username" class="welcome-text">欢迎，{{ username }}</span>
        <el-button type="primary" @click="showAddDialog">添加游戏</el-button>
        <el-button type="danger" @click="handleLogout">退出登录</el-button>
      </div>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-section">
      <el-form :inline="true" :model="filterForm" class="filter-form">
        <el-form-item label="平台">
          <el-select v-model="filterForm.platform" placeholder="选择平台" clearable>
            <el-option 
              v-for="platform in platformList" 
              :key="platform.id" 
              :label="platform.platform" 
              :value="platform.platform"
            ></el-option>
            <el-option 
              v-for="combo in platformCombos" 
              :key="'combo-' + combo" 
              :label="combo" 
              :value="combo"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="维度">
          <el-select v-model="filterForm.dimension" placeholder="选择维度" clearable>
            <el-option label="2D" value="2D"></el-option>
            <el-option label="3D" value="3D"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="分类">
          <el-select v-model="filterForm.category" placeholder="选择分类" clearable>
            <el-option 
              v-for="type in gameTypeList" 
              :key="type.id" 
              :label="type.gameType" 
              :value="type.gameType"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="游戏库">
          <el-select v-model="filterForm.gameLibrary" placeholder="选择游戏库" clearable>
            <el-option 
              v-for="library in gameLibraryList" 
              :key="library.id" 
              :label="library.gameLibrary" 
              :value="library.gameLibrary"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="handleQuery">查询</el-button>
          <el-button @click="resetFilter">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 游戏列表 -->
    <GameList 
      :games="games" 
      :loading="loading"
      @edit="handleEdit"
      @delete="handleDelete"
    />

    <!-- 分页组件 -->
    <div class="pagination-section">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[5, 10, 20, 50]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total">
      </el-pagination>
    </div>

    <!-- 添加/编辑游戏对话框 -->
    <GameForm
      :visible.sync="dialogVisible"
      :game-data="currentGame"
      :is-edit="isEdit"
      :platform-list="platformList"
      :game-type-list="gameTypeList"
      :game-library-list="gameLibraryList"
      @save="handleSave"
    />
  </div>
</template>

<script>
import GameList from './GameList.vue'
import GameForm from './GameForm.vue'
import { gameApi } from '../api/gameApi.js'

export default {
  name: 'GameManagement',
  components: {
    GameList,
    GameForm
  },
  data() {
    return {
      games: [],
      loading: false,
      dialogVisible: false,
      isEdit: false,
      currentGame: null,
      filterForm: {
        platform: '',
        dimension: '',
        category: '',
        gameLibrary: ''
      },
      // 分页数据
      pagination: {
        currentPage: 1,
        pageSize: 5,
        total: 0
      },
      platformList: [], // 平台列表数据
      gameTypeList: [], // 游戏分类列表
      gameLibraryList: [], // 游戏库列表
      username: '' // 当前登录用户
    }
  },
  computed: {
    // 生成平台组合选项
    platformCombos() {
      const combos = []
      const platforms = this.platformList.map(p => p.platform)
      
      // 生成所有可能的组合
      for (let i = 0; i < platforms.length; i++) {
        for (let j = i + 1; j < platforms.length; j++) {
          combos.push(`${platforms[i]}/${platforms[j]}`)
        }
      }
      
      // 如果有3个或更多平台，生成三平台组合
      if (platforms.length >= 3) {
        for (let i = 0; i < platforms.length - 2; i++) {
          for (let j = i + 1; j < platforms.length - 1; j++) {
            for (let k = j + 1; k < platforms.length; k++) {
              combos.push(`${platforms[i]}/${platforms[j]}/${platforms[k]}`)
            }
          }
        }
      }
      
      return combos
    }
  },
  mounted() {
    this.loadPlatformList()
    this.loadGameTypeList()
    this.loadGameLibraryList()
    this.loadGames()
  },
  methods: {
    // 获取平台列表
    async loadPlatformList() {
      try {
        const response = await gameApi.getAllPlatforms()
        if (response.code === 200) {
          this.platformList = response.data || []
        } else {
          console.error('获取平台列表失败:', response.msg)
        }
      } catch (error) {
        console.error('获取平台列表失败:', error)
      }
    },

    // 获取游戏分类列表
    async loadGameTypeList() {
      try {
        const response = await gameApi.getAllGameTypes()
        if (response.code === 200) {
          this.gameTypeList = response.data || []
        } else {
          console.error('获取游戏分类列表失败:', response.msg)
        }
      } catch (error) {
        console.error('获取游戏分类列表失败:', error)
      }
    },

    // 获取游戏库列表
    async loadGameLibraryList() {
      try {
        const response = await gameApi.getAllGameLibraries()
        if (response.code === 200) {
          this.gameLibraryList = response.data || []
        } else {
          console.error('获取游戏库列表失败:', response.msg)
        }
      } catch (error) {
        console.error('获取游戏库列表失败:', error)
      }
    },

    // 加载游戏列表
    async loadGames() {
      this.loading = true
      try {
        const params = {
          ...this.filterForm,
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize
        }
        const response = await gameApi.getGamesList(params)
        if (response.code === 200) {
          // 处理分页数据 - 新格式：{records: [], total: 6, current: 1, size: 2, pages: 3}
          if (response.data && typeof response.data === 'object' && response.data.records) {
            this.games = response.data.records || []
            this.pagination.total = response.data.total || 0
            this.pagination.currentPage = response.data.current || 1
            this.pagination.pageSize = response.data.size || 10
          } else {
            // 如果后端返回的仍然是数组格式，保持兼容
            this.games = response.data || []
            this.pagination.total = this.games.length
          }
        } else {
          this.$message.error(response.msg || '获取游戏列表失败')
        }
      } catch (error) {
        console.error('加载游戏列表失败:', error)
        this.$message.error('加载游戏列表失败')
      } finally {
        this.loading = false
      }
    },

    // 显示添加对话框
    showAddDialog() {
      this.isEdit = false
      this.currentGame = null
      this.dialogVisible = true
    },

    // 处理编辑
    handleEdit(game) {
      this.isEdit = true
      this.currentGame = { ...game }
      this.dialogVisible = true
    },

    // 处理删除
    async handleDelete(game) {
      try {
        await this.$confirm('确定要删除这个游戏吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await gameApi.deleteGame(game.id)
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.loadGames()
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除游戏失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    // 处理保存
    handleSave(updatedData) {
      // 关闭对话框
      this.dialogVisible = false
      
      // 优化页面刷新逻辑
      if (updatedData) {
        if (this.isEdit) {
          // 编辑模式：直接更新games数组中对应的数据，提供更好的用户体验
          const index = this.games.findIndex(game => game.id === updatedData.id)
          if (index !== -1) {
            this.games.splice(index, 1, updatedData)
          }
        } else {
          // 添加模式：重新加载整个列表以确保数据一致性
          this.loadGames()
        }
      } else {
        // 兼容旧的调用方式，重新加载整个列表
        this.loadGames()
      }
    },

    // 查询
    handleQuery() {
      this.pagination.currentPage = 1 // 查询时重置到第一页
      this.loadGames()
    },

    // 重置筛选条件
    resetFilter() {
      this.filterForm = {
        platform: '',
        dimension: '',
        category: '',
        gameLibrary: ''
      }
      this.pagination.currentPage = 1 // 重置到第一页
      this.loadGames()
    },

    // 分页大小改变
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize
      this.pagination.currentPage = 1 // 重置到第一页
      this.loadGames()
    },

    // 当前页改变
    handleCurrentChange(currentPage) {
      this.pagination.currentPage = currentPage
      this.loadGames()
    },

    // 处理退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 清除本地存储的token和用户名
        localStorage.removeItem('token')
        localStorage.removeItem('username')
        // 跳转到登录页面
        this.$router.push('/login')
      }).catch(() => {
        // 用户取消退出
        this.$message.info('已取消退出')
      })
    }
  }
}
</script>

<style scoped>
.game-management {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.welcome-text {
  margin-right: 10px;
  color: #606266;
  font-size: 14px;
}

.header h1 {
  margin: 0;
  color: #333;
}

.filter-section {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.filter-form {
  margin: 0;
}

.filter-form .el-form-item {
  margin-bottom: 10px;
}

.filter-form .el-form-item:last-child {
  margin-bottom: 0;
}

.pagination-section {
  margin-top: 20px;
  text-align: center;
  padding: 20px 0;
}
</style>
